import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useEnquiryModal } from '@/hooks/useEnquiryModal';
import { getMangoBySlug } from '@/mocks/mangoes';
import ImageLightbox from '@/components/base/ImageLightbox';
import FadeInSection from '@/components/base/FadeInSection';

export default function MangoDetail() {
  const { variety } = useParams<{ variety: string }>();
  const mango = variety ? getMangoBySlug(variety) : undefined;
  const { openEnquiry } = useEnquiryModal();
  const [activeImage, setActiveImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (!mango) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-3xl mx-auto px-4 py-24 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-5">
            <i className="ri-error-warning-line text-foreground-400 text-2xl"></i>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">
            Mango variety not found
          </h1>
          <p className="text-foreground-500 text-sm mb-6">
            The variety you are looking for is not available. Please browse all our mango varieties.
          </p>
          <Link
            to="/mangoes"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            View All Mangoes
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Breadcrumb */}
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-4">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/products" className="hover:text-primary-600 transition-colors cursor-pointer">Products</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/mangoes" className="hover:text-primary-600 transition-colors cursor-pointer">Mangoes</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-900 font-medium">{mango.displayName}</span>
            </nav>
          </div>
        </div>

        <section className="py-10 md:py-14 px-4 md:px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              {/* Images */}
              <FadeInSection>
                <div>
                  <div
                    className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-background-100 cursor-pointer border border-background-200"
                    onClick={() => setLightboxOpen(true)}
                  >
                    <img
                      src={mango.images[activeImage]}
                      alt={`${mango.displayName} - Image ${activeImage + 1}`}
                      title={`${mango.displayName} - Bassi Enterprises`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold shadow-sm ${
                          mango.available ? 'bg-primary-500 text-background-50' : 'bg-amber-500 text-background-50'
                        }`}
                      >
                        <i className={mango.available ? 'ri-checkbox-circle-fill text-xs' : 'ri-time-line text-xs'}></i>
                        {mango.availabilityNote}
                      </span>
                    </div>
                  </div>
                  {mango.images.length > 1 && (
                    <div className="flex gap-3 mt-3">
                      {mango.images.map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => setActiveImage(idx)}
                          className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                            idx === activeImage ? 'border-primary-500' : 'border-background-200 hover:border-primary-300'
                          }`}
                        >
                          <img src={img} alt={`${mango.displayName} thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </FadeInSection>

              {/* Details */}
              <FadeInSection delay={100}>
                <div>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-accent-100 text-accent-800 text-xs font-semibold rounded-full mb-3">
                    {mango.badge}
                  </span>
                  <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-2">
                    {mango.displayName}
                  </h1>
                  <p className="text-foreground-500 text-base font-medium mb-5">{mango.tagline}</p>

                  <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-7">
                    {mango.description}
                  </p>

                  {/* Grades table */}
                  <div className="bg-background-100 rounded-2xl p-5 md:p-6 mb-5">
                    <h3 className="font-heading text-sm font-bold text-foreground-900 mb-3 flex items-center gap-2">
                      <i className="ri-price-tag-3-line text-primary-600"></i>
                      Grades &amp; Indicative Pricing
                    </h3>
                    <div className="space-y-2">
                      {mango.grades.map((g) => (
                        <div key={g.label} className="flex items-center justify-between bg-background-50 rounded-lg px-4 py-2.5 border border-background-200">
                          <span className="text-sm text-foreground-700 font-medium">{g.label}</span>
                          <span className="text-sm font-bold text-foreground-900">{g.priceRange}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-foreground-400 italic mt-3">
                      Prices are indicative MRP and vary by grade and season. Bulk rates available on enquiry.
                    </p>
                  </div>

                  {/* Quick facts */}
                  <div className="grid grid-cols-2 gap-3 mb-7">
                    <div className="p-3 rounded-xl bg-background-100 border border-background-200">
                      <div className="flex items-center gap-2 mb-1">
                        <i className="ri-calendar-check-line text-accent-600 text-sm"></i>
                        <span className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider">Shelf Life</span>
                      </div>
                      <p className="text-sm font-semibold text-foreground-900">{mango.shelfLife}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-background-100 border border-background-200">
                      <div className="flex items-center gap-2 mb-1">
                        <i className="ri-restaurant-line text-primary-600 text-sm"></i>
                        <span className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider">Best For</span>
                      </div>
                      <p className="text-sm font-semibold text-foreground-900">{mango.bestFor}</p>
                    </div>
                  </div>

                  {/* CTAs */}
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={() => openEnquiry(`${mango.displayName} (${mango.name})`)}
                      className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer shadow-sm hover:shadow-md"
                    >
                      <i className="ri-whatsapp-fill text-lg"></i>
                      Enquire for Bulk Supply
                    </button>
                    <Link
                      to="/mangoes"
                      className="inline-flex items-center gap-2 px-6 py-3.5 border border-background-300 text-foreground-700 hover:bg-background-100 text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-arrow-left-line"></i>
                      All Varieties
                    </Link>
                  </div>
                </div>
              </FadeInSection>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {lightboxOpen && (
        <ImageLightbox
          images={mango.images}
          currentIndex={activeImage}
          alt={`${mango.displayName} - Bassi Enterprises`}
          onClose={() => setLightboxOpen(false)}
          onPrev={() => setActiveImage((prev) => (prev - 1 + mango.images.length) % mango.images.length)}
          onNext={() => setActiveImage((prev) => (prev + 1) % mango.images.length)}
        />
      )}
    </div>
  );
}
